import { useState, useEffect } from 'react';
import { Clock, User, CheckCircle, AlertCircle, FileText, MessageSquare, Edit2 } from 'lucide-react';

/**
 * Activity Timeline Component (Frontend)
 * Displays audit trail of document changes
 */
export default function ActivityTimeline({ document, activities = [] }) {
  const [sortedActivities, setSortedActivities] = useState([]);

  useEffect(() => {
    // Combine document events with custom activities
    const timeline = [];

    // Add document creation
    if (document.createdAt) {
      timeline.push({
        type: 'created',
        title: 'Document created',
        description: `"${document.name}" was created`,
        timestamp: new Date(document.createdAt),
        user: document.createdBy?.firstName ? `${document.createdBy.firstName} ${document.createdBy.lastName}` : 'System',
        icon: FileText,
      });
    }

    // Add version history
    document.versions?.forEach((version) => {
      timeline.push({
        type: 'version',
        title: `Version ${version.versionNumber} uploaded`,
        description: version.notes || 'New version',
        timestamp: new Date(version.uploadedAt),
        user: version.uploadedBy?.firstName ? `${version.uploadedBy.firstName} ${version.uploadedBy.lastName}` : 'System',
        icon: FileText,
        metadata: `${version.fileName} • ${formatFileSize(version.fileSize)}`,
      });
    });

    // Add approval info
    if (document.approval && document.approval.reviewedAt) {
      timeline.push({
        type: document.approval.status === 'approved' ? 'approved' : 'denied',
        title: document.approval.status === 'approved' ? 'Document approved' : 'Document denied',
        description: document.approval.comments || `Document ${document.approval.status}`,
        timestamp: new Date(document.approval.reviewedAt),
        user: document.approval.reviewedBy?.firstName ? `${document.approval.reviewedBy.firstName} ${document.approval.reviewedBy.lastName}` : 'System',
        icon: document.approval.status === 'approved' ? CheckCircle : AlertCircle,
      });
    }

    // Add status transitions from workflow
    if (document.workflow?.sentForReviewAt) {
      timeline.push({
        type: 'status_change',
        title: 'Sent for review',
        description: 'Document moved to client_review',
        timestamp: new Date(document.workflow.sentForReviewAt),
        user: document.workflow.sentForReviewBy?.firstName ? `${document.workflow.sentForReviewBy.firstName} ${document.workflow.sentForReviewBy.lastName}` : 'System',
        icon: Edit2,
      });
    }

    // Add custom activities
    timeline.push(...(activities || []));

    // Sort by timestamp descending (newest first)
    setSortedActivities(timeline.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
  }, [document, activities]);

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatTime = (date) => {
    const d = new Date(date);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'approved':
        return 'bg-green-50 border-l-4 border-green-500';
      case 'denied':
        return 'bg-red-50 border-l-4 border-red-500';
      case 'version':
        return 'bg-blue-50 border-l-4 border-blue-500';
      case 'comment':
        return 'bg-purple-50 border-l-4 border-purple-500';
      default:
        return 'bg-secondary-50 border-l-4 border-secondary-300';
    }
  };

  const getIconColor = (type) => {
    switch (type) {
      case 'approved':
        return 'text-green-600';
      case 'denied':
        return 'text-red-600';
      case 'version':
        return 'text-blue-600';
      case 'comment':
        return 'text-purple-600';
      default:
        return 'text-secondary-600';
    }
  };

  if (!sortedActivities || sortedActivities.length === 0) {
    return (
      <div className="text-center py-8 text-secondary-500">
        <Clock size={32} className="mx-auto mb-2 opacity-50" />
        <p>No activity yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {sortedActivities.map((activity, index) => {
        const IconComponent = activity.icon || Clock;
        return (
          <div
            key={`${activity.type}-${index}`}
            className={`p-4 rounded-lg ${getActivityColor(activity.type)}`}
          >
            <div className="flex gap-4">
              <div className={`flex-shrink-0 mt-1 ${getIconColor(activity.type)}`}>
                <IconComponent size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h4 className="font-semibold text-secondary-900">{activity.title}</h4>
                    <p className="text-sm text-secondary-600 mt-1">{activity.description}</p>
                    {activity.metadata && (
                      <p className="text-xs text-secondary-500 mt-1">{activity.metadata}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2 text-xs text-secondary-500">
                  <User size={14} />
                  <span>{activity.user}</span>
                  <span>•</span>
                  <Clock size={14} />
                  <span>{formatTime(activity.timestamp)}</span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
